import { DataField, StrongValue } from '@if/mui'
import { Card, CardContent, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material'
import { ReactNode } from 'react'

export interface FundingSummaryRow {
  key: string
  label: string
  value?: ReactNode
  emphasis?: boolean
  // Renders the label alone as a bold section subtitle: no value, no skeleton.
  header?: boolean
}

export interface WorksheetFundingSummaryProps {
  title: string
  headerRight?: ReactNode
  isComputing: boolean
  // One inner array per rendered column; columns share the width equally from md up.
  columns: FundingSummaryRow[][]
}

const renderRowValue = ({ value, emphasis }: FundingSummaryRow, isComputing: boolean): ReactNode => {
  if (isComputing) return <Skeleton width={80} />
  if (emphasis && value != null && value !== '') return <StrongValue>{value}</StrongValue>
  return value
}

const WorksheetFundingSummary = ({ title, headerRight, isComputing, columns }: WorksheetFundingSummaryProps) => {
  const columnSize = 12 / columns.length

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          {headerRight && (
            <Typography variant="caption" color="text.secondary">
              {headerRight}
            </Typography>
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={{ xs: 1, md: 4 }}>
          {columns.map((rows, columnIndex) => (
            <Grid key={rows[0]?.key ?? String(columnIndex)} size={{ xs: 12, md: columnSize }}>
              <Stack spacing={1.5}>
                {rows.map((row) =>
                  row.header ? (
                    <Typography key={row.key} variant="subtitle2" fontWeight={700} sx={{ pt: 1 }}>
                      {row.label}
                    </Typography>
                  ) : (
                    <DataField key={row.key} label={row.label} value={renderRowValue(row, isComputing)} />
                  ),
                )}
              </Stack>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  )
}

export default WorksheetFundingSummary
